import type {BoardColumn, BoardRow} from '@github-ui/board-core'
import {Banner} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {useVisibleFieldIdentifiers} from '../../../contexts/views/VisibleFieldsProvider'
import {useDashboardIssueSidePanel} from '../../../hooks/use-dashboard-issue-side-panel'
import {type FieldDefinition, ItemType} from '../../saved-view-options/fields'
import {SavedViewBoard} from '../SavedViewBoard'
import type {SavedViewsBoardAdapter} from '../types'
import type {IssuesBoardMetadata} from './board-metadata'
import type {BoardAxisLabels, BoardPayloadItem} from './board-payload'
import {BoardCard, type BoardFilter, type BoardItemIdentifier} from './BoardCard'
import {BoardColumnHeading} from './BoardColumnHeading'
import {BoardRowHeading} from './BoardRowHeading'
import {useAnnounceDeferredCardFieldFailures, useLoadedDeferredCardFields} from './deferred-card-fields'
import type {BoardDeferredFields} from './deferred-payload'
import {IssuesBoardBlankslate} from './IssuesBoardBlankslate'

/**
 * Empty, so `j` and `k` in the side panel do nothing. The cards are paged into the board per
 * cell, and this host holds no ordered list of them to step through.
 */
const NO_NAVIGABLE_ISSUES: Parameters<typeof useDashboardIssueSidePanel>[0]['navigableIssues'] = []

/**
 * Refetching the route after a panel edit re-keys the board and drops every cell page loaded
 * since, and the index may not have the edit yet. Updating the card in place belongs with
 * github/views#105.
 */
const NO_REFRESH = () => {}

/**
 * A saved view's issues as a board served by the view's own endpoints. An issue title opens the
 * issue in the side panel, and a pull request title opens its own page.
 */
export function IssuesSavedViewBoard({
  adapter,
  query,
  isNavigating,
  fieldDefinitions,
  onQueryChange,
  viewportAnchor,
  boardClassName,
}: {
  adapter: SavedViewsBoardAdapter<BoardPayloadItem, IssuesBoardMetadata, BoardAxisLabels, BoardDeferredFields>
  query: string
  /** Which custom fields are single-selects. Until it loads, cards draw no custom fields. */
  fieldDefinitions: readonly FieldDefinition[] | undefined
  /**
   * The route is fetching the response this board is built from. Applying a grouping or a
   * swimlane rebuilds both axes, so the board on screen is drawn as a placeholder until
   * the new one arrives. The same fact the list layout is handed as `isLoading`.
   */
  isNavigating: boolean
  /** Shows the view for a query, keeping its layout and other view parameters. */
  onQueryChange: (query: string) => void
  viewportAnchor?: 'document' | 'viewport'
  boardClassName?: string
}) {
  const {deferredFieldsSource} = adapter
  useAnnounceDeferredCardFieldFailures(deferredFieldsSource)
  const visibleFieldIdentifiers = useVisibleFieldIdentifiers()
  const onLoadedItemsChange = useLoadedDeferredCardFields(
    deferredFieldsSource,
    visibleFieldIdentifiers,
    fieldDefinitions,
  )
  const {onIssueSelect, listContainerProps, sidePanel, deletionErrorNotice} = useDashboardIssueSidePanel({
    navigableIssues: NO_NAVIGABLE_ISSUES,
    refreshList: NO_REFRESH,
  })

  const onItemSelect = useCallback(
    (itemType: ItemType, item: BoardItemIdentifier, returnFocusElement: HTMLElement) => {
      switch (itemType) {
        case ItemType.ISSUE:
          onIssueSelect(item, returnFocusElement)
          return true
        case ItemType.PULL_REQUEST:
          return false
        default: {
          const _exhaustive: never = itemType
          return _exhaustive
        }
      }
    },
    [onIssueSelect],
  )

  const filter = useMemo<BoardFilter>(() => ({query, apply: onQueryChange}), [onQueryChange, query])

  const renderCard = useCallback(
    (item: BoardPayloadItem) => (
      <BoardCard
        item={item}
        visibleFieldIdentifiers={visibleFieldIdentifiers}
        fieldDefinitions={fieldDefinitions}
        onItemSelect={onItemSelect}
        filter={filter}
        deferredFieldsSource={deferredFieldsSource}
      />
    ),
    [visibleFieldIdentifiers, fieldDefinitions, onItemSelect, filter, deferredFieldsSource],
  )

  const renderColumnHeader = useCallback(
    (column: BoardColumn, metadata: IssuesBoardMetadata) => (
      <BoardColumnHeading column={column} labels={metadata.columnLabels} />
    ),
    [],
  )

  const renderRowHeader = useCallback(
    (row: BoardRow, metadata: IssuesBoardMetadata) => <BoardRowHeading row={row} labels={metadata.rowLabels} />,
    [],
  )

  const renderEmptyState = useCallback(
    (metadata: IssuesBoardMetadata) => <IssuesBoardBlankslate query={query} metadata={metadata} />,
    [query],
  )

  return (
    <>
      {deletionErrorNotice && (
        <Banner
          variant="critical"
          title={deletionErrorNotice.title}
          hideTitle
          description={deletionErrorNotice.message}
          className="tmp-mt-3"
        />
      )}
      {/* The side panel focuses this region when it closes and the clicked title is gone. */}
      <div {...listContainerProps}>
        <SavedViewBoard
          adapter={adapter}
          // The query the reader is looking at, which the route sends as the view's own when the
          // URL carries none. It identifies the board's cache rather than the request, so date
          // tokens stay unresolved: the server resolves the query it was sent, and a key naming
          // something else would claim a request that was never made.
          options={{query}}
          renderCard={renderCard}
          onLoadedItemsChange={onLoadedItemsChange}
          renderColumnHeader={renderColumnHeader}
          renderRowHeader={renderRowHeader}
          renderEmptyState={renderEmptyState}
          isLoading={isNavigating}
          viewportAnchor={viewportAnchor}
          boardClassName={boardClassName}
        />
      </div>
      {sidePanel}
    </>
  )
}
